import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';

import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../../constants';

// Three-stripe flags, drawn in CSS the way the design source does it, so the
// swatch is a 22x15 rectangle rather than a letterboxed square icon. English
// has no stripe form and gets a small SVG Union Jack in the same box.
const FLAGS = {
    ro: ['#002B7F', '#FCD116', '#CE1126'],
    fr: ['#002395', '#FFFFFF', '#ED2939'],
};

const LANGUAGES = [
    { code: 'ro', label: 'RO' },
    { code: 'en', label: 'EN' },
    { code: 'fr', label: 'FR' },
];

const useStyles = makeStyles(() => ({
    button: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '9px',
        minHeight: '46px',
        padding: '0 14px',
        border: `1px solid ${REDESIGN_COLORS.navBorder}`,
        borderRadius: '12px',
        background: REDESIGN_COLORS.white,
        color: REDESIGN_COLORS.navText,
        fontFamily: REDESIGN_FONTS.body,
        fontSize: '0.98rem',
        fontWeight: REDESIGN_FONTS.weightStrong,
        cursor: 'pointer',
        '&:hover': {
            borderColor: REDESIGN_COLORS.primary,
        },
    },
    flag: {
        display: 'inline-flex',
        width: '22px',
        height: '15px',
        borderRadius: '2px',
        overflow: 'hidden',
        flexShrink: 0,
    },
    stripe: {
        flex: 1,
    },
    jack: {
        display: 'block',
        width: '100%',
        height: '100%',
    },
    chevron: {
        display: 'block',
    },
    menuItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        fontFamily: REDESIGN_FONTS.body,
        fontSize: '0.98rem',
        fontWeight: REDESIGN_FONTS.weightStrong,
        color: REDESIGN_COLORS.navText,
    },
}));

// The Union Jack: blue field, white diagonals with the red saltire over them,
// then the white and red cross. The saltire's counterchange is left out; it
// is invisible at 22x15.
const UnionJack = ({ className }) => (
    <svg viewBox="0 0 60 30" preserveAspectRatio="none" aria-hidden="true" className={className}>
        <rect width="60" height="30" fill="#012169" />
        <path d="M0,0 L60,30 M60,0 L0,30" stroke="#FFFFFF" strokeWidth="6" />
        <path d="M0,0 L60,30 M60,0 L0,30" stroke="#C8102E" strokeWidth="2" />
        <path d="M30,0 V30 M0,15 H60" stroke="#FFFFFF" strokeWidth="10" />
        <path d="M30,0 V30 M0,15 H60" stroke="#C8102E" strokeWidth="6" />
    </svg>
);

export const Flag = ({ code, className }) => {
    const classes = useStyles();
    const stripes = FLAGS[code];

    return (
        <span aria-hidden="true" className={[classes.flag, className].filter(Boolean).join(' ')}>
            {stripes ? (
                stripes.map((color, i) => (
                    <span key={i} className={classes.stripe} style={{ background: color }} />
                ))
            ) : (
                <UnionJack className={classes.jack} />
            )}
        </span>
    );
};

/**
 * The redesign's language selector: a bordered pill showing the current
 * language, opening a menu. Handlers match the existing LanguageMenu so the
 * GA logging and redirect behaviour are unchanged.
 */
const LanguagePill = ({ language, handleRo, handleEn, handleFr }) => {
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = React.useState(null);

    const current = (language || 'ro').toLowerCase();
    const active = LANGUAGES.find((l) => l.code === current) || LANGUAGES[0];
    const handlers = { ro: handleRo, en: handleEn, fr: handleFr };

    const open = (event) => setAnchorEl(event.currentTarget);
    const close = () => setAnchorEl(null);

    const pick = (code) => (event) => {
        close();
        const handler = handlers[code];
        if (handler) handler(event);
    };

    const location = window.location;

    return (
        <>
            <button
                type="button"
                className={classes.button}
                aria-label={'Language: ' + active.label}
                aria-haspopup="true"
                onClick={open}
            >
                <Flag code={active.code} />
                {active.label}
                <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={REDESIGN_COLORS.navText}
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    className={classes.chevron}
                >
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>
            <Menu anchorEl={anchorEl} keepMounted disableScrollLock open={Boolean(anchorEl)} onClose={close}>
                {LANGUAGES.map((lang) => (
                    <MenuItem key={lang.code} selected={lang.code === active.code} onClick={pick(lang.code)}>
                        <a
                            href={`${location.protocol}//${location.host}/${lang.code}`}
                            onClick={pick(lang.code)}
                            className={classes.menuItem}
                        >
                            <Flag code={lang.code} />
                            {lang.label}
                        </a>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export default LanguagePill;
